import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Card,
  Grid,
  Typography,
  IconButton,
  Skeleton,
  Alert,
  Collapse,
  Button,
  Chip,
  Stack,
  Tooltip,
} from '@mui/material';
import { 
  FilterList as FilterIcon, 
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CalendarToday as CalendarTodayIcon,
  Business as BusinessIcon,
  AccountTree as AccountTreeIcon,
  CheckBox as CheckBoxIcon,
  DisabledByDefault as DisabledByDefaultIcon
} from '@mui/icons-material';
import { fetchFilterOptions, fetchDependentFilters } from '../../services/api';
import { FilterOptions, SelectedFilters } from '../../types/filters';
import { useTheme } from '@mui/material/styles';
import { formatErrorForDisplay } from '@/utils/errorUtils';
import { MultiSelect } from '../ui/MultiSelect';
import { SingleSelect } from '../ui/SingleSelect';
import { components } from 'react-select';
import { FilterSectionHeader } from './FilterSectionHeader';
import { AppliedFiltersChips, FilterChip } from './AppliedFiltersChips';

interface Option {
  value: string;
  label: string;
}

interface FiltersPanelProps {
  onFiltersChange: (filters: SelectedFilters) => void;
  compact?: boolean;
}

// Chola Insurance theme colors
const CHOLA_COLORS = {
  primary: '#003087', // Deep Blue
  secondary: '#E31837', // Red
  accent: '#FFB81C', // Yellow
  background: '#FFFFFF',
  text: '#333333',
  lightBlue: '#0066CC',
  grey: '#F5F5F5',
  hover: '#004AAD' // Darker blue for hover states
};

// Month name mapping
const MONTH_NAMES = {
  1: 'January',
  2: 'February',
  3: 'March',
  4: 'April',
  5: 'May',
  6: 'June',
  7: 'July',
  8: 'August',
  9: 'September',
  10: 'October',
  11: 'November',
  12: 'December'
};

export function FiltersPanel({ onFiltersChange, compact = false }: FiltersPanelProps) {
  const theme = useTheme();
  const isFirstLoad = useRef(true);
  const [expanded, setExpanded] = useState(false);
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
  const [dependentOptions, setDependentOptions] = useState<FilterOptions | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({
    selectedMonth: undefined,
    selectedYear: undefined,
    selectedLOB: [],
    selectedProducts: [],
    selectedSubProducts: [],
    selectedChannels: [],
    selectedSubChannels: [],
    dateRange: {
      startDate: null,
      endDate: null,
    },
  });
  // Track the last applied filters (the ones that were sent to parent components)
  const [appliedFilters, setAppliedFilters] = useState<SelectedFilters | null>(null);
  // Track whether filters have been changed but not applied
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Function to determine the current financial year from available options
  const getCurrentFinancialYear = (years: { id: string; name: string; start_year: number; end_year: number }[]) => {
    if (!years || years.length === 0) return undefined;
    
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1; // JavaScript months are 0-indexed
    const currentCalendarYear = currentDate.getFullYear();
    
    // In India, financial year runs from April 1 to March 31
    // If current month is January to March (1-3), we are in the previous year's FY
    // If current month is April to December (4-12), we are in the current year's FY
    
    let targetStartYear, targetEndYear;
    if (currentMonth >= 4) {
      // April to December - current FY starts in current calendar year
      targetStartYear = currentCalendarYear;
      targetEndYear = currentCalendarYear + 1;
    } else {
      // January to March - current FY started in previous calendar year
      targetStartYear = currentCalendarYear - 1;
      targetEndYear = currentCalendarYear;
    }
    
    // Find the financial year that matches our target start and end years
    const currentFY = years.find(y => 
      y.start_year === targetStartYear && y.end_year === targetEndYear
    );
    
    // If exact match not found, return the most recent year
    return currentFY ? currentFY.id : years[0].id;
  };
  
  // Function to determine the current month
  const getCurrentMonth = (months: { id: number; name: string }[]) => {
    if (!months || months.length === 0) return undefined;
    
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1; // JavaScript months are 0-indexed
    
    // Find the month that matches the current calendar month
    const monthOption = months.find(m => m.id === currentMonth);
    
    // If the current month is not in the list, return the latest available month
    return monthOption ? monthOption.id.toString() : months[months.length - 1].id.toString();
  };

  // Load initial filter options on component mount
  useEffect(() => {
    const loadFilterData = async () => {
      try {
        // Only load data if this is the first render
        if (!isFirstLoad.current) return;
        
        setLoading(true);
        const optionsResponse = await fetchFilterOptions();
        setFilterOptions(optionsResponse);
        setDependentOptions(null); // Initialize with null instead of optionsResponse
        setError(null);
        
        // Auto-select current financial year
        const currentFY = getCurrentFinancialYear(optionsResponse.years);
        
        if (currentFY) {
          // Set the year
          const updatedFilters = {
            ...selectedFilters,
            selectedYear: currentFY
          };
          setSelectedFilters(updatedFilters);
          
          // For the selected year, get the available months directly from filterOptions
          const selectedYearObj = optionsResponse.years.find(y => y.id === currentFY);
          if (selectedYearObj) {
            // Determine which months are available for this financial year
            // For April-March financial year, show all 12 months
            const availableMonths = optionsResponse.months;
            
            // Auto-select current month if it's available
            const currentMonth = getCurrentMonth(availableMonths);
            if (currentMonth) {
              const updatedFiltersWithMonth = {
                ...updatedFilters,
                selectedYear: currentFY,
                selectedMonth: currentMonth
              };
              setSelectedFilters(updatedFiltersWithMonth);
              
              // For initial load, apply filters immediately but only once
              setAppliedFilters(updatedFiltersWithMonth);
              
              // Check if this is the first load to avoid multiple API calls
              if (isFirstLoad.current) {
                triggerFiltersChange(updatedFiltersWithMonth);
                isFirstLoad.current = false;
              }
            }
          }
        }
      } catch (err) {
        setError('Failed to load filter options. Please try again.');
        console.error('Error loading filter options:', err);
      } finally {
        setLoading(false);
      }
    };

    loadFilterData();
  }, []);
  
  // After financial year is selected, calculate available months and auto-select current month if needed
  useEffect(() => {
    if (selectedFilters.selectedYear && filterOptions) {
      // Find the selected year object
      const selectedYearObj = filterOptions.years.find(y => y.id === selectedFilters.selectedYear);
      
      if (selectedYearObj) {
        // For a financial year (e.g., 2023-2024), all 12 months are available
        // We can use the months from filterOptions directly
        const availableMonths = filterOptions.months;
        
        // Update dependent options with available months
        setDependentOptions(prev => ({
          ...prev || filterOptions,
          months: availableMonths
        }));
        
        // Only auto-select month if it's not already selected
        if (!selectedFilters.selectedMonth) {
          const currentMonth = getCurrentMonth(availableMonths);
          
          if (currentMonth) {
            const updatedFilters = {
              ...selectedFilters,
              selectedMonth: currentMonth
            };
            setSelectedFilters(updatedFilters);
            triggerFiltersChange(updatedFilters);
          }
        }
      }
    }
  }, [selectedFilters.selectedYear, filterOptions]);

  const hasAnyFilterSelected = () => {
    return selectedFilters.selectedChannels.length > 0 ||
           selectedFilters.selectedSubChannels.length > 0 ||
           selectedFilters.selectedProducts.length > 0 ||
           selectedFilters.selectedSubProducts.length > 0 ||
           selectedFilters.selectedLOB.length > 0;
  };

  // Update dependent filters when selections change - except for month selection
  useEffect(() => {
    const updateDependentFilters = async () => {
      try {
        if (hasAnyFilterSelected() || selectedFilters.selectedYear) {
          // Pass all selected values for each filter type
          const channel = selectedFilters.selectedChannels.length > 0 ? selectedFilters.selectedChannels : undefined;
          const subchannel = selectedFilters.selectedSubChannels.length > 0 ? selectedFilters.selectedSubChannels : undefined;
          const product = selectedFilters.selectedProducts.length > 0 ? selectedFilters.selectedProducts : undefined;
          const sub_product = selectedFilters.selectedSubProducts.length > 0 ? selectedFilters.selectedSubProducts : undefined;
          const lob = selectedFilters.selectedLOB.length > 0 ? selectedFilters.selectedLOB : undefined;
          const year = selectedFilters.selectedYear;

          
          const dependentFilters = await fetchDependentFilters(
            channel,
            subchannel,
            product,
            sub_product,
            lob,
            year
          );
          
          // Preserve the months from our calculations, but update other dependent options
          setDependentOptions(prev => ({
            ...dependentFilters,
            months: prev?.months || filterOptions?.months || []
          }));
        } else {
          // If no filters are selected, reset to all options
          setDependentOptions(null);
        }
      } catch (err) {
        console.error('Error updating dependent filters:', err);
        // Don't show error to user, just log it
      }
    };

    updateDependentFilters();
  }, [
    selectedFilters.selectedChannels,
    selectedFilters.selectedSubChannels,
    selectedFilters.selectedProducts,
    selectedFilters.selectedLOB,
    selectedFilters.selectedYear
  ]);

  // Effect to validate selected month when dependent options change
  useEffect(() => {
    if (dependentOptions && selectedFilters.selectedMonth) {
      // Check if the currently selected month is in the available months
      const availableMonthIds = dependentOptions.months.map(m => m.id.toString());
      if (!availableMonthIds.includes(selectedFilters.selectedMonth)) {
        
        // Selected month is not available, clear it
        const updatedFilters = {
          ...selectedFilters,
          selectedMonth: undefined
        };
        setSelectedFilters(updatedFilters);
        triggerFiltersChange(updatedFilters);
      }
    }
  }, [dependentOptions]);

  // Effect to ensure filters are propagated to parent when auto-selected
  useEffect(() => {
    // Skip this effect if it's not the first load or if required filters aren't selected
    if (!selectedFilters.selectedYear || !selectedFilters.selectedMonth || initialLoadComplete || !isFirstLoad.current) {
      return;
    }
    
    setAppliedFilters(selectedFilters);
    triggerFiltersChange(selectedFilters);
    setFiltersChanged(false);
    isFirstLoad.current = false;
  }, [selectedFilters.selectedYear, selectedFilters.selectedMonth, initialLoadComplete]);

  // Auto-select filters that have only one option available - only on initial load
  useEffect(() => {
    if (!filterOptions || loading) return;
    
    const updatedFilters = { ...selectedFilters };
    let hasChanges = false;
    
    // Check for single option in years
    if (filterOptions.years.length === 1 && !selectedFilters.selectedYear) {
      updatedFilters.selectedYear = filterOptions.years[0].id;
      hasChanges = true;
    }
    
    // Check for single option in other filters
    if (filterOptions.lob.length === 1 && selectedFilters.selectedLOB.length === 0) {
      updatedFilters.selectedLOB = [filterOptions.lob[0]];
      hasChanges = true;
    }
    
    if (filterOptions.product_group.length === 1 && selectedFilters.selectedProducts.length === 0) {
      updatedFilters.selectedProducts = [filterOptions.product_group[0]];
      hasChanges = true;
    }
    
    if (filterOptions.sub_product.length === 1 && selectedFilters.selectedSubProducts.length === 0) {
      updatedFilters.selectedSubProducts = [filterOptions.sub_product[0]];
      hasChanges = true;
    }
    
    if (filterOptions.channel.length === 1 && selectedFilters.selectedChannels.length === 0) {
      updatedFilters.selectedChannels = [filterOptions.channel[0]];
      hasChanges = true;
    }
    
    if (filterOptions.subchannel.length === 1 && selectedFilters.selectedSubChannels.length === 0) {
      updatedFilters.selectedSubChannels = [filterOptions.subchannel[0]];
      hasChanges = true;
    }
    
    // After setting dependencies, we'll check dependent options in another effect
    if (hasChanges) {
      setSelectedFilters(updatedFilters);
      triggerFiltersChange(updatedFilters);
    }
    
    // Mark initial load as complete so this doesn't run again
    setInitialLoadComplete(true);
  }, [filterOptions, loading]);
  
  // Effect for auto-selecting dependent values with single options - only on initial load
  useEffect(() => {
    if (!dependentOptions || !filterOptions || initialLoadComplete) return;
    
    const updatedFilters = { ...selectedFilters };
    let hasChanges = false;
    
    // Auto-select month if there's only one option and year is selected
    if (dependentOptions.months.length === 1 && selectedFilters.selectedYear && !selectedFilters.selectedMonth) {
      updatedFilters.selectedMonth = dependentOptions.months[0].id.toString();
      hasChanges = true;
    }
    
    // Check for single option in lob, product, channel, etc.
    if (dependentOptions.lob.length === 1 && selectedFilters.selectedLOB.length === 0) {
      updatedFilters.selectedLOB = [dependentOptions.lob[0]];
      hasChanges = true;
    }
    
    if (dependentOptions.product_group.length === 1 && selectedFilters.selectedProducts.length === 0) {
      updatedFilters.selectedProducts = [dependentOptions.product_group[0]];
      hasChanges = true;
    }
    
    if (dependentOptions.sub_product.length === 1 && selectedFilters.selectedSubProducts.length === 0) {
      updatedFilters.selectedSubProducts = [dependentOptions.sub_product[0]];
      hasChanges = true;
    }
    
    if (dependentOptions.channel.length === 1 && selectedFilters.selectedChannels.length === 0) {
      updatedFilters.selectedChannels = [dependentOptions.channel[0]];
      hasChanges = true;
    }
    
    if (dependentOptions.subchannel.length === 1 && selectedFilters.selectedSubChannels.length === 0) {
      updatedFilters.selectedSubChannels = [dependentOptions.subchannel[0]];
      hasChanges = true;
    }
    
    if (hasChanges) {
      setSelectedFilters(updatedFilters);
      triggerFiltersChange(updatedFilters);
      setInitialLoadComplete(true);
    }
  }, [dependentOptions, selectedFilters.selectedYear, initialLoadComplete]);

  const handleMultiFilterChange = (filterName: keyof SelectedFilters, selectedOptions: Option[]) => {
    const newFilters = {
      ...selectedFilters,
      [filterName]: selectedOptions.map(option => option.value)
    };

    // Clear dependent filters based on the changed filter
    switch (filterName) {
      case 'selectedChannels':
        // If channels are changed, clear all dependent filters
        newFilters.selectedSubChannels = [];
        newFilters.selectedLOB = [];
        newFilters.selectedProducts = [];
        newFilters.selectedSubProducts = [];
        break;
      case 'selectedSubChannels':
        // If subchannels are changed, clear LOB, products and sub-products
        newFilters.selectedLOB = [];
        newFilters.selectedProducts = [];
        newFilters.selectedSubProducts = [];
        break;
      case 'selectedLOB':
        // If LOB is changed, clear products and sub-products
        newFilters.selectedProducts = [];
        newFilters.selectedSubProducts = [];
        break;
      case 'selectedProducts':
        // If products are changed, clear sub-products
        newFilters.selectedSubProducts = [];
        break;
    }

    setSelectedFilters(newFilters);
    
    // Mark filters as changed but don't apply them yet
    setFiltersChanged(true);
    
    setInitialLoadComplete(true);
  };

  const handleSingleFilterChange = (filterName: 'selectedMonth' | 'selectedYear', selectedOption: Option | null) => {
    // Start with the basic updates
    const newFilters = {
      ...selectedFilters,
      [filterName]: selectedOption ? selectedOption.value : undefined
    };
    
    // If year changed and there's a month selected, we may need to clear the month
    if (filterName === 'selectedYear') {
      // If deselecting year, always clear month
      if (!selectedOption) {
        newFilters.selectedMonth = undefined;
        
      }
      // If selecting a new year, we'll let the useEffect update the dependent options
      // The month validity will be checked when user tries to select a month
    }
    
    setSelectedFilters(newFilters);
    
    // Mark filters as changed but don't apply them yet
    setFiltersChanged(true);
    
    setInitialLoadComplete(true);
  };

  const handleApplyFilters = () => {
    // Prevent calling the API if filters haven't actually changed from what's already applied
    if (JSON.stringify(selectedFilters) === JSON.stringify(appliedFilters)) {
      
      setFiltersChanged(false);
      return;
    }
    
    // Apply the current filters
    setAppliedFilters(selectedFilters);
    triggerFiltersChange(selectedFilters);
    setFiltersChanged(false);
    
    // Collapse the filters panel
    setExpanded(false);
  };

  const handleClearFilters = () => {
    window.location.href = window.location.href.split('?')[0] + '?cacheBust=' + new Date().getTime();
  };

  const getSelectedSingleOption = (value: string | undefined, options: Option[]) => {
    if (!value) return null;
    const selectedOption = options.find(option => option.value === value);
    return selectedOption || null;
  };

  const getMonthOptions = () => {
    // If we have dependent options (i.e., year is selected), use those months
    // Otherwise fall back to the original filter options
    const availableMonths = 
      (dependentOptions?.months && dependentOptions.months.length > 0) 
        ? dependentOptions.months 
        : (filterOptions?.months || []);
    
    return availableMonths.map(month => ({
      value: month.id.toString(),
      label: month.name
    }));
  };

  const getYearOptions = () => {
    return (filterOptions?.years || []).map(year => ({
      value: year.id,
      label: year.name,
      start_year: year.start_year,
      end_year: year.end_year
    }));
  };

  // Function to remove a single filter
  const handleRemoveFilter = (group: keyof SelectedFilters, value: string) => {
    if (group === 'selectedMonth' || group === 'selectedYear') {
      const newFilters = {
        ...selectedFilters,
        [group]: undefined
      };
      setSelectedFilters(newFilters);
      triggerFiltersChange(newFilters);
    } else if (Array.isArray(selectedFilters[group])) {
      const newFilters = {
        ...selectedFilters,
        [group]: (selectedFilters[group] as string[]).filter(item => item !== value)
      };
      setSelectedFilters(newFilters);
      triggerFiltersChange(newFilters);
    }
    setInitialLoadComplete(true);
  };

  // Keep the Select All function but remove the separate Clear Selection function since it's built into the MultiSelect now
  const handleSelectAll = (filterType: 'channel' | 'business', filterName: keyof SelectedFilters) => {
    const newFilters = { ...selectedFilters };
    
    if (filterType === 'channel') {
      if (filterName === 'selectedChannels') {
        newFilters.selectedChannels = options.channel;
      } else if (filterName === 'selectedSubChannels') {
        newFilters.selectedSubChannels = options.subchannel;
      }
    } else if (filterType === 'business') {
      if (filterName === 'selectedLOB') {
        newFilters.selectedLOB = options.lob;
      } else if (filterName === 'selectedProducts') {
        newFilters.selectedProducts = options.product_group;
      } else if (filterName === 'selectedSubProducts') {
        newFilters.selectedSubProducts = options.sub_product;
      }
    }
    
    setSelectedFilters(newFilters);
    triggerFiltersChange(newFilters);
    setInitialLoadComplete(true);
  };

  // Count how many filters are currently applied
  const countAppliedFilters = useCallback(() => {
    if (!appliedFilters) return 0;
    
    let count = 0;
    if (appliedFilters.selectedMonth) count++;
    if (appliedFilters.selectedYear) count++;
    if (appliedFilters.selectedChannels?.length > 0) count++;
    if (appliedFilters.selectedSubChannels?.length > 0) count++;
    if (appliedFilters.selectedLOB?.length > 0) count++;
    if (appliedFilters.selectedProducts?.length > 0) count++;
    if (appliedFilters.selectedSubProducts?.length > 0) count++;
    
    return count;
  }, [appliedFilters]);
  
  // Create a memoized wrapper for onFiltersChange to prevent unnecessary API calls
  const triggerFiltersChange = useCallback((filters: SelectedFilters) => {
    
    onFiltersChange(filters);
  }, [onFiltersChange]);
  
  // Check if any filters are currently applied
  const hasActiveFilters = useMemo(() => {
    return countAppliedFilters() > 0;
  }, [countAppliedFilters]);
  
  // Function to determine which calendar year to send based on month and selected financial year
  const calculateCalendarYear = () => {
    if (!selectedFilters.selectedYear || !selectedFilters.selectedMonth) return null;
    
    const selectedMonth = parseInt(selectedFilters.selectedMonth);
    const selectedYearOption = filterOptions?.years.find(
      y => y.id === selectedFilters.selectedYear
    );
    
    if (!selectedYearOption) return null;
    
    // Months 1-3 (Jan-Mar) use end_year, months 4-12 (Apr-Dec) use start_year
    return selectedMonth >= 1 && selectedMonth <= 3 
      ? selectedYearOption.end_year.toString()
      : selectedYearOption.start_year.toString();
  };

  // Generate applied filter chips (moved before conditional returns to keep hook order stable)
  const appliedFilterChips = useMemo((): FilterChip[] => {
    // Compute options inside useMemo to ensure consistency
    const computedOptions = {
      months: filterOptions?.months || [],
      years: filterOptions?.years || [],
      lob: filterOptions?.lob || [],
      product_group: filterOptions?.product_group || [],
      sub_product: filterOptions?.sub_product || [],
      channel: filterOptions?.channel || [],
      subchannel: filterOptions?.subchannel || []
    };

    // Apply dependent options if available
    if (hasAnyFilterSelected() && dependentOptions) {
      computedOptions.months = dependentOptions.months || computedOptions.months;
      computedOptions.years = dependentOptions.years || computedOptions.years;
      
      if (selectedFilters.selectedChannels.length > 0) {
        computedOptions.subchannel = dependentOptions.subchannel || computedOptions.subchannel;
        
        if (selectedFilters.selectedSubChannels.length > 0) {
          computedOptions.lob = dependentOptions.lob || computedOptions.lob;
          
          if (selectedFilters.selectedLOB.length > 0) {
            computedOptions.product_group = dependentOptions.product_group || computedOptions.product_group;
            
            if (selectedFilters.selectedProducts.length > 0) {
              computedOptions.sub_product = dependentOptions.sub_product || computedOptions.sub_product;
            } else {
              computedOptions.sub_product = [];
            }
          } else {
            computedOptions.product_group = [];
            computedOptions.sub_product = [];
          }
        } else {
          computedOptions.lob = [];
          computedOptions.product_group = [];
          computedOptions.sub_product = [];
        }
      } else {
        computedOptions.subchannel = [];
        computedOptions.lob = [];
        computedOptions.product_group = [];
        computedOptions.sub_product = [];
      }
    }

    const chips: FilterChip[] = [];
    
    // Year filter
    if (selectedFilters.selectedYear) {
      const yearOption = computedOptions.years.find(y => y.id === selectedFilters.selectedYear);
      const yearLabel = yearOption ? yearOption.name : selectedFilters.selectedYear;
      chips.push({
        key: 'year',
        label: `Year: ${yearLabel}`,
        onDelete: () => {
          const newFilters = { ...selectedFilters };
          delete newFilters.selectedYear;
          // Also clear month when year is cleared
          delete newFilters.selectedMonth;
          setSelectedFilters(newFilters);
          triggerFiltersChange(newFilters);
        }
      });
    }
    
    // Month filter
    if (selectedFilters.selectedMonth) {
      const monthNum = parseInt(selectedFilters.selectedMonth);
      const monthName = MONTH_NAMES[monthNum as keyof typeof MONTH_NAMES] || selectedFilters.selectedMonth;
      chips.push({
        key: 'month',
        label: `Month: ${monthName}`,
        onDelete: () => {
          const newFilters = { ...selectedFilters };
          delete newFilters.selectedMonth;
          setSelectedFilters(newFilters);
          triggerFiltersChange(newFilters);
        }
      });
    }
    
    // Channel filter
    if (selectedFilters.selectedChannels?.length) {
      // Check if all channels are selected
      const isAllSelected = computedOptions.channel.length > 0 && 
                           computedOptions.channel.length === selectedFilters.selectedChannels.length;
      if (isAllSelected) {
        chips.push({
          key: 'channel-all',
          label: 'Channel: All',
          onDelete: () => {
            const newFilters = {
              ...selectedFilters,
              selectedChannels: [],
              selectedSubChannels: [],
              selectedLOB: [],
              selectedProducts: [],
              selectedSubProducts: []
            };
            setSelectedFilters(newFilters);
            triggerFiltersChange(newFilters);
          }
        });
      } else {
        selectedFilters.selectedChannels.forEach((channel, idx) => {
          chips.push({
            key: `channel_${idx}`,
            label: `Channel: ${channel}`,
            onDelete: () => {
              handleRemoveFilter('selectedChannels', channel);
            }
          });
        });
      }
    }
    
    // SubChannel filter
    if (selectedFilters.selectedSubChannels?.length) {
      const isAllSelected = computedOptions.subchannel.length > 0 && 
                           computedOptions.subchannel.length === selectedFilters.selectedSubChannels.length;
      if (isAllSelected) {
        chips.push({
          key: 'subchannel-all',
          label: 'SubChannel: All',
          onDelete: () => {
            const newFilters = {
              ...selectedFilters,
              selectedSubChannels: [],
              selectedLOB: [],
              selectedProducts: [],
              selectedSubProducts: []
            };
            setSelectedFilters(newFilters);
            triggerFiltersChange(newFilters);
          }
        });
      } else {
        selectedFilters.selectedSubChannels.forEach((subchannel, idx) => {
          chips.push({
            key: `subchannel_${idx}`,
            label: `SubChannel: ${subchannel}`,
            onDelete: () => {
              handleRemoveFilter('selectedSubChannels', subchannel);
            }
          });
        });
      }
    }
    
    // LOB filter
    if (selectedFilters.selectedLOB?.length) {
      const isAllSelected = computedOptions.lob.length > 0 && 
                           computedOptions.lob.length === selectedFilters.selectedLOB.length;
      if (isAllSelected) {
        chips.push({
          key: 'lob-all',
          label: 'LOB: All',
          onDelete: () => {
            const newFilters = {
              ...selectedFilters,
              selectedLOB: [],
              selectedProducts: [],
              selectedSubProducts: []
            };
            setSelectedFilters(newFilters);
            triggerFiltersChange(newFilters);
          }
        });
      } else {
        selectedFilters.selectedLOB.forEach((lob, idx) => {
          chips.push({
            key: `lob_${idx}`,
            label: `LOB: ${lob}`,
            onDelete: () => {
              handleRemoveFilter('selectedLOB', lob);
            }
          });
        });
      }
    }
    
    // Product filter
    if (selectedFilters.selectedProducts?.length) {
      const isAllSelected = computedOptions.product_group.length > 0 && 
                           computedOptions.product_group.length === selectedFilters.selectedProducts.length;
      if (isAllSelected) {
        chips.push({
          key: 'product-all',
          label: 'Product: All',
          onDelete: () => {
            const newFilters = {
              ...selectedFilters,
              selectedProducts: [],
              selectedSubProducts: []
            };
            setSelectedFilters(newFilters);
            triggerFiltersChange(newFilters);
          }
        });
      } else {
        selectedFilters.selectedProducts.forEach((product, idx) => {
          chips.push({
            key: `product_${idx}`,
            label: `Product: ${product}`,
            onDelete: () => {
              handleRemoveFilter('selectedProducts', product);
            }
          });
        });
      }
    }
    
    // SubProduct (Classification) filter
    if (selectedFilters.selectedSubProducts?.length) {
      const isAllSelected = computedOptions.sub_product.length > 0 && 
                           computedOptions.sub_product.length === selectedFilters.selectedSubProducts.length;
      if (isAllSelected) {
        chips.push({
          key: 'classification-all',
          label: 'Classification: All',
          onDelete: () => {
            const newFilters = {
              ...selectedFilters,
              selectedSubProducts: []
            };
            setSelectedFilters(newFilters);
            triggerFiltersChange(newFilters);
          }
        });
      } else {
        selectedFilters.selectedSubProducts.forEach((subproduct, idx) => {
          chips.push({
            key: `classification_${idx}`,
            label: `Classification: ${subproduct}`,
            onDelete: () => {
              handleRemoveFilter('selectedSubProducts', subproduct);
            }
          });
        });
      }
    }
    
    return chips;
  }, [selectedFilters, filterOptions, dependentOptions, handleRemoveFilter, triggerFiltersChange]);

  // Calculate calendar year based on selected month and financial year
  const calendarYear = calculateCalendarYear();

  if (loading) {
    return (
      <Card sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          {[1, 2, 3].map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item}>
              <Skeleton variant="rectangular" height={56} />
            </Grid>
          ))}
        </Grid>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {formatErrorForDisplay(error)}
      </Alert>
    );
  }

  if (!filterOptions) {
    return null;
  }

  const options = {
    months: filterOptions?.months || [],
    years: filterOptions?.years || [],
    lob: filterOptions?.lob || [],
    product_group: filterOptions?.product_group || [],
    sub_product: filterOptions?.sub_product || [],
    channel: filterOptions?.channel || [],
    subchannel: filterOptions?.subchannel || []
  };

  // Only use dependent options if they exist and filters are selected
  if (hasAnyFilterSelected() && dependentOptions) {
    options.months = dependentOptions.months || options.months;
    options.years = dependentOptions.years || options.years;
    
    // Update options based on selected filters in a cascading manner
    if (selectedFilters.selectedChannels.length > 0) {
      // If channels are selected, show dependent subchannels
      options.subchannel = dependentOptions.subchannel || options.subchannel;
      
      if (selectedFilters.selectedSubChannels.length > 0) {
        // If subchannels are selected, show dependent LOBs
        options.lob = dependentOptions.lob || options.lob;
        
        if (selectedFilters.selectedLOB.length > 0) {
          // If LOBs are selected, show dependent products
          options.product_group = dependentOptions.product_group || options.product_group;
          
          if (selectedFilters.selectedProducts.length > 0) {
            // If products are selected, show dependent sub-products
            options.sub_product = dependentOptions.sub_product || options.sub_product;
          } else {
            // If no products selected, clear sub-products
            options.sub_product = [];
          }
        } else {
          // If no LOBs selected, clear products and sub-products
          options.product_group = [];
          options.sub_product = [];
        }
      } else {
        // If no subchannels selected, clear LOBs, products and sub-products
        options.lob = [];
        options.product_group = [];
        options.sub_product = [];
      }
    } else {
      // If no channels selected, clear all dependent filters
      options.subchannel = [];
      options.lob = [];
      options.product_group = [];
      options.sub_product = [];
    }
  }

  // NOTE: `calendarYear` and `appliedFilterChips` are now defined earlier in the component
  // to keep hook order stable. The logic below is intentionally removed here.

  /*
  // Calculate calendar year based on selected month and financial year
  const calendarYear = calculateCalendarYear();

  // Generate applied filter chips (moved after options computation)
  const appliedFilterChips = useMemo((): FilterChip[] => {
    // Compute options inside useMemo to ensure consistency
    const computedOptions = {
      months: filterOptions?.months || [],
      years: filterOptions?.years || [],
      lob: filterOptions?.lob || [],
      product_group: filterOptions?.product_group || [],
      sub_product: filterOptions?.sub_product || [],
      channel: filterOptions?.channel || [],
      subchannel: filterOptions?.subchannel || []
    };

    // Apply dependent options if available
    if (hasAnyFilterSelected() && dependentOptions) {
      computedOptions.months = dependentOptions.months || computedOptions.months;
      computedOptions.years = dependentOptions.years || computedOptions.years;
      
      if (selectedFilters.selectedChannels.length > 0) {
        computedOptions.subchannel = dependentOptions.subchannel || computedOptions.subchannel;
        
        if (selectedFilters.selectedSubChannels.length > 0) {
          computedOptions.lob = dependentOptions.lob || computedOptions.lob;
          
          if (selectedFilters.selectedLOB.length > 0) {
            computedOptions.product_group = dependentOptions.product_group || computedOptions.product_group;
            
            if (selectedFilters.selectedProducts.length > 0) {
              computedOptions.sub_product = dependentOptions.sub_product || computedOptions.sub_product;
            } else {
              computedOptions.sub_product = [];
            }
          } else {
            computedOptions.product_group = [];
            computedOptions.sub_product = [];
          }
        } else {
          computedOptions.lob = [];
          computedOptions.product_group = [];
          computedOptions.sub_product = [];
        }
      } else {
        computedOptions.subchannel = [];
        computedOptions.lob = [];
        computedOptions.product_group = [];
        computedOptions.sub_product = [];
      }
    }

    const chips: FilterChip[] = [];
    
    // Year filter
    if (selectedFilters.selectedYear) {
      const yearOption = computedOptions.years.find(y => y.id === selectedFilters.selectedYear);
      const yearLabel = yearOption ? yearOption.name : selectedFilters.selectedYear;
      chips.push({
        key: 'year',
        label: `Year: ${yearLabel}`,
        onDelete: () => {
          const newFilters = { ...selectedFilters };
          delete newFilters.selectedYear;
          // Also clear month when year is cleared
          delete newFilters.selectedMonth;
          setSelectedFilters(newFilters);
          triggerFiltersChange(newFilters);
        }
      });
    }
    
    // Month filter
    if (selectedFilters.selectedMonth) {
      const monthNum = parseInt(selectedFilters.selectedMonth);
      const monthName = MONTH_NAMES[monthNum as keyof typeof MONTH_NAMES] || selectedFilters.selectedMonth;
      chips.push({
        key: 'month',
        label: `Month: ${monthName}`,
        onDelete: () => {
          const newFilters = { ...selectedFilters };
          delete newFilters.selectedMonth;
          setSelectedFilters(newFilters);
          triggerFiltersChange(newFilters);
        }
      });
    }
    
    // Channel filter
    if (selectedFilters.selectedChannels?.length) {
      // Check if all channels are selected
      const isAllSelected = computedOptions.channel.length > 0 && 
                           computedOptions.channel.length === selectedFilters.selectedChannels.length;
      if (isAllSelected) {
        chips.push({
          key: 'channel-all',
          label: 'Channel: All',
          onDelete: () => {
            const newFilters = {
              ...selectedFilters,
              selectedChannels: [],
              selectedSubChannels: [],
              selectedLOB: [],
              selectedProducts: [],
              selectedSubProducts: []
            };
            setSelectedFilters(newFilters);
            triggerFiltersChange(newFilters);
          }
        });
      } else {
        selectedFilters.selectedChannels.forEach((channel, idx) => {
          chips.push({
            key: `channel_${idx}`,
            label: `Channel: ${channel}`,
            onDelete: () => {
              handleRemoveFilter('selectedChannels', channel);
            }
          });
        });
      }
    }
    
    // SubChannel filter
    if (selectedFilters.selectedSubChannels?.length) {
      const isAllSelected = computedOptions.subchannel.length > 0 && 
                           computedOptions.subchannel.length === selectedFilters.selectedSubChannels.length;
      if (isAllSelected) {
        chips.push({
          key: 'subchannel-all',
          label: 'SubChannel: All',
          onDelete: () => {
            const newFilters = {
              ...selectedFilters,
              selectedSubChannels: [],
              selectedLOB: [],
              selectedProducts: [],
              selectedSubProducts: []
            };
            setSelectedFilters(newFilters);
            triggerFiltersChange(newFilters);
          }
        });
      } else {
        selectedFilters.selectedSubChannels.forEach((subchannel, idx) => {
          chips.push({
            key: `subchannel_${idx}`,
            label: `SubChannel: ${subchannel}`,
            onDelete: () => {
              handleRemoveFilter('selectedSubChannels', subchannel);
            }
          });
        });
      }
    }
    
    // LOB filter
    if (selectedFilters.selectedLOB?.length) {
      const isAllSelected = computedOptions.lob.length > 0 && 
                           computedOptions.lob.length === selectedFilters.selectedLOB.length;
      if (isAllSelected) {
        chips.push({
          key: 'lob-all',
          label: 'LOB: All',
          onDelete: () => {
            const newFilters = {
              ...selectedFilters,
              selectedLOB: [],
              selectedProducts: [],
              selectedSubProducts: []
            };
            setSelectedFilters(newFilters);
            triggerFiltersChange(newFilters);
          }
        });
      } else {
        selectedFilters.selectedLOB.forEach((lob, idx) => {
          chips.push({
            key: `lob_${idx}`,
            label: `LOB: ${lob}`,
            onDelete: () => {
              handleRemoveFilter('selectedLOB', lob);
            }
          });
        });
      }
    }
    
    // Product filter
    if (selectedFilters.selectedProducts?.length) {
      const isAllSelected = computedOptions.product_group.length > 0 && 
                           computedOptions.product_group.length === selectedFilters.selectedProducts.length;
      if (isAllSelected) {
        chips.push({
          key: 'product-all',
          label: 'Product: All',
          onDelete: () => {
            const newFilters = {
              ...selectedFilters,
              selectedProducts: [],
              selectedSubProducts: []
            };
            setSelectedFilters(newFilters);
            triggerFiltersChange(newFilters);
          }
        });
      } else {
        selectedFilters.selectedProducts.forEach((product, idx) => {
          chips.push({
            key: `product_${idx}`,
            label: `Product: ${product}`,
            onDelete: () => {
              handleRemoveFilter('selectedProducts', product);
            }
          });
        });
      }
    }
    
    // SubProduct (Classification) filter
    if (selectedFilters.selectedSubProducts?.length) {
      const isAllSelected = computedOptions.sub_product.length > 0 && 
                           computedOptions.sub_product.length === selectedFilters.selectedSubProducts.length;
      if (isAllSelected) {
        chips.push({
          key: 'classification-all',
          label: 'Classification: All',
          onDelete: () => {
            const newFilters = {
              ...selectedFilters,
              selectedSubProducts: []
            };
            setSelectedFilters(newFilters);
            triggerFiltersChange(newFilters);
          }
        });
      } else {
        selectedFilters.selectedSubProducts.forEach((subproduct, idx) => {
          chips.push({
            key: `classification_${idx}`,
            label: `Classification: ${subproduct}`,
            onDelete: () => {
              handleRemoveFilter('selectedSubProducts', subproduct);
            }
          });
        });
      }
    }
    
    return chips;
  }, [selectedFilters, filterOptions, dependentOptions, handleRemoveFilter, triggerFiltersChange]);
  */
  
  return (
    <Card 
      sx={{ 
        mb: compact ? 0.5 : 3,
        mt: 0,
        transition: theme.transitions.create(['box-shadow']),
        '&:hover': {
          boxShadow: expanded ? theme.shadows[4] : theme.shadows[1],
        },
        zIndex: 9999,
        ...(compact && {
          // Additional styles for compact mode
          '& .MuiPaper-root': { boxShadow: 'none' },
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none'
        }),
        // Mobile responsive styles
        '& .MuiGrid-container': {
          '& .MuiGrid-item': {
            '& .MuiBox-root': {
              '& .MuiTypography-subtitle2': {
                fontSize: { xs: '0.7rem', sm: '0.875rem' }
              }
            }
          }
        }
      }}
    >
      <FilterSectionHeader
        expanded={expanded}
        onToggle={() => setExpanded(!expanded)}
        appliedCount={countAppliedFilters()}
        onClearAll={handleClearFilters}
        hasChanges={filtersChanged}
        onApply={handleApplyFilters}
        compact={compact}
        title="Filters"
      />

      {/* Applied Filters Display */}
      {hasActiveFilters && !expanded && (
        <AppliedFiltersChips chips={appliedFilterChips} compact={compact} />
      )}

      <Collapse in={expanded}>
        <Box sx={{ p: compact ? 1 : 2, bgcolor: 'background.paper' }}>
          <Grid container spacing={compact ? 1 : 2}>
            {/* Time Period Section */}
            <Grid item xs={12} md={compact ? 12 : 6}>
              <Box sx={{ mb: compact ? 1 : { xs: 2, md: 3 } }}>
                <Typography variant="subtitle2" sx={{ 
                  mb: compact ? 0.5 : 1.5, 
                  color: CHOLA_COLORS.primary,
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: compact ? '0.65rem' : '0.875rem'
                }}>
                  <CalendarTodayIcon sx={{ fontSize: compact ? 12 : 18 }} />
                  Time Period
                </Typography>
                <Grid container spacing={compact ? 0.5 : 1.5}>
                  <Grid item xs={6}>
                    <SingleSelect
                      compact={compact}
                      label="Year"
                      options={getYearOptions()}
                      value={getSelectedSingleOption(selectedFilters.selectedYear, getYearOptions())}
                      onChange={(selected) => handleSingleFilterChange('selectedYear', selected)}
                      placeholder="Select year..."
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <SingleSelect
                      compact={compact}
                      label="Month"
                      options={getMonthOptions()}
                      value={getSelectedSingleOption(selectedFilters.selectedMonth, getMonthOptions())}
                      onChange={(selected) => handleSingleFilterChange('selectedMonth', selected)}
                      placeholder="Select month..."
                      disabled={!selectedFilters.selectedYear}
                    />
                  </Grid>
                </Grid>
                {selectedFilters.selectedYear && selectedFilters.selectedMonth && calendarYear && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontSize: compact ? '0.6rem' : undefined }}>
                    Calendar Year: {calendarYear}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Channel Filters Section */}
            <Grid item xs={12} md={compact ? 12 : 6}>
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  mb: compact ? 0.5 : 1.5, 
                  color: CHOLA_COLORS.primary,
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: compact ? '0.65rem' : '0.875rem'
                }}>
                  <AccountTreeIcon sx={{ fontSize: compact ? 12 : 18 }} />
                  Channel Filters
                </Typography>
                <Grid container spacing={compact ? 0.5 : 1.5}>
                  <Grid item xs={6}>
                    <MultiSelect
                      compact={compact}
                      label="Channel"
                      options={options.channel.map(channel => ({ value: channel, label: channel }))}
                      value={selectedFilters.selectedChannels.map(channel => ({ value: channel, label: channel }))}
                      onChange={(selected) => handleMultiFilterChange('selectedChannels', selected)}
                      placeholder="Select channels..."
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <MultiSelect
                      compact={compact}
                      label="Sub Channel"
                      options={options.subchannel.map(subchannel => ({ value: subchannel, label: subchannel }))}
                      value={selectedFilters.selectedSubChannels.map(subchannel => ({ value: subchannel, label: subchannel }))}
                      onChange={(selected) => handleMultiFilterChange('selectedSubChannels', selected)}
                      placeholder="Select sub channels..."
                      disabled={selectedFilters.selectedChannels.length === 0}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Business Filters Section */}
            <Grid item xs={12} md={compact ? 12 : 12} sx={{ mx: 'auto' }}>
              <Box sx={{ mb: compact ? 0.5 : { xs: 2, md: 3 } }}>
                <Typography variant="subtitle2" sx={{ 
                  mb: compact ? 0.5 : 1.5, 
                  color: CHOLA_COLORS.primary,
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: compact ? '0.65rem' : '0.875rem'
                }}>
                  <BusinessIcon sx={{ fontSize: compact ? 12 : 18 }} />
                  Business Filters
                </Typography>
                <Grid container spacing={compact ? 0.5 : 1.5}>
                  <Grid item xs={6} md={4}>
                    <MultiSelect
                      compact={compact}
                      label="LOB"
                      options={options.lob.map(lob => ({ value: lob, label: lob }))}
                      value={selectedFilters.selectedLOB.map(lob => ({ value: lob, label: lob }))}
                      onChange={(selected) => handleMultiFilterChange('selectedLOB', selected)}
                      placeholder="Select LOB..."
                      disabled={selectedFilters.selectedChannels.length === 0 || selectedFilters.selectedSubChannels.length === 0}
                    />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <MultiSelect
                      compact={compact}
                      label="Product"
                      options={options.product_group.map(product => ({ value: product, label: product }))}
                      value={selectedFilters.selectedProducts.map(product => ({ value: product, label: product }))}
                      onChange={(selected) => handleMultiFilterChange('selectedProducts', selected)}
                      placeholder="Select products..."
                      disabled={selectedFilters.selectedChannels.length === 0 || 
                               selectedFilters.selectedSubChannels.length === 0 || 
                               selectedFilters.selectedLOB.length === 0}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MultiSelect
                      compact={compact}
                      label="Classification"
                      options={options.sub_product.map(subProduct => ({ value: subProduct, label: subProduct }))}
                      value={selectedFilters.selectedSubProducts.map(subProduct => ({ value: subProduct, label: subProduct }))}
                      onChange={(selected) => handleMultiFilterChange('selectedSubProducts', selected)}
                      placeholder="Select classification..."
                      disabled={selectedFilters.selectedChannels.length === 0 || 
                               selectedFilters.selectedSubChannels.length === 0 || 
                               selectedFilters.selectedLOB.length === 0 || 
                               selectedFilters.selectedProducts.length === 0}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>
            
            {/* Apply Filters Button Section */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                mt: compact ? 0.5 : 2,
                borderTop: `1px solid ${theme.palette.divider}`,
                pt: compact ? 0.5 : 2
              }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  disabled={!filtersChanged}
                  onClick={handleApplyFilters}
                  startIcon={<FilterIcon />}
                  sx={{
                    backgroundColor: CHOLA_COLORS.primary,
                    '&:hover': {
                      backgroundColor: CHOLA_COLORS.hover
                    },
                    '&.Mui-disabled': {
                      backgroundColor: theme.palette.action.disabledBackground,
                      color: theme.palette.action.disabled
                    },
                    ...(compact && {
                      fontSize: '0.6rem',
                      py: 0.4,
                      px: 1,
                      minHeight: '32px'
                    })
                  }}
                >
                  Apply Filters
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Card>
  );
} 